import { useEffect, useState } from "react";
import { AdminLayout } from "./components/AdminLayout";
import { AuthGate } from "./components/AuthGate";
import { LoadingState } from "./components/States";
import { useToast } from "./components/Toast";
import { adminApi, SESSION_KEY } from "./lib/api";
import { ContentPage } from "./pages/ContentPage";
import { InquiriesPage } from "./pages/InquiriesPage";
import { MediaPage } from "./pages/MediaPage";
import { OverviewPage } from "./pages/OverviewPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { AdminSection } from "./types";

const SECTIONS: AdminSection[] = [
  "overview",
  "content",
  "inquiries",
  "media",
  "settings"
];

function sectionFromHash(): AdminSection {
  const value = window.location.hash.replace(/^#\/?/, "") as AdminSection;
  return SECTIONS.includes(value) ? value : "overview";
}

export function App() {
  const { showToast } = useToast();
  const [authState, setAuthState] = useState<
    "checking" | "locked" | "authenticated"
  >("checking");
  const [section, setSection] = useState<AdminSection>(sectionFromHash);
  const [hasUnsavedWork, setHasUnsavedWork] = useState(false);

  useEffect(() => {
    let active = true;
    const existingKey = sessionStorage.getItem(SESSION_KEY);
    if (!existingKey) {
      setAuthState("locked");
      return;
    }
    adminApi
      .dashboard(existingKey)
      .then(() => {
        if (active) setAuthState("authenticated");
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_KEY);
        if (active) setAuthState("locked");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function handleHashChange() {
      setSection(sectionFromHash());
    }
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    function handleUnauthorized() {
      sessionStorage.removeItem(SESSION_KEY);
      setHasUnsavedWork(false);
      setAuthState("locked");
      showToast("The admin key expired or was rejected. The console is locked.", "error");
    }
    window.addEventListener("atalay:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("atalay:unauthorized", handleUnauthorized);
  }, [showToast]);

  useEffect(() => {
    function handleDirtyState(event: Event) {
      setHasUnsavedWork(Boolean((event as CustomEvent<boolean>).detail));
    }
    window.addEventListener("atalay:dirty-state", handleDirtyState);
    return () =>
      window.removeEventListener("atalay:dirty-state", handleDirtyState);
  }, []);

  function navigate(nextSection: AdminSection) {
    if (nextSection === section) return;
    if (
      hasUnsavedWork &&
      !window.confirm("Leave this section and discard its unsaved changes?")
    ) {
      return;
    }
    setHasUnsavedWork(false);
    setSection(nextSection);
    window.history.replaceState(null, "", `#/${nextSection}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => document.getElementById("main-content")?.focus(), 0);
  }

  function logout() {
    if (
      hasUnsavedWork &&
      !window.confirm("Clear the key and discard unsaved changes?")
    ) {
      return;
    }
    sessionStorage.removeItem(SESSION_KEY);
    setHasUnsavedWork(false);
    setAuthState("locked");
    showToast("Access key cleared. The console is locked.", "info");
  }

  if (authState === "checking") {
    return (
      <main className="access-page">
        <section className="access-card access-card--checking">
          <div className="access-card__brand">ATALAY</div>
          <LoadingState label="Verifying this session" />
        </section>
      </main>
    );
  }

  if (authState === "locked") {
    return <AuthGate onAuthenticated={() => setAuthState("authenticated")} />;
  }

  return (
    <AdminLayout section={section} onNavigate={navigate} onLogout={logout}>
      {section === "overview" && <OverviewPage onNavigate={navigate} />}
      {section === "content" && <ContentPage />}
      {section === "inquiries" && <InquiriesPage />}
      {section === "media" && <MediaPage />}
      {section === "settings" && <SettingsPage />}
    </AdminLayout>
  );
}
